import { Injectable, signal, inject } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { RightBridgeApiService } from '../services/right-bridge-api.service';
import { SessionStorageService } from '../services/session-storage.service';
import { Observable, Subject } from 'rxjs';
import { EnvironmentService } from '../services/environment.service';
import {
  ApiResponse,
  ResultString,
  StringResult,
} from '../models/general-models';
import { Profile } from '../models/profile';
import { GroupProfile } from '../models/global.model';

@Injectable({ providedIn: 'root' })
export class FormService {
  private rbs = inject(RightBridgeApiService);
  private ss = inject(SessionStorageService);
  private envSvc = inject(EnvironmentService);

  updateExternalCalcValue = signal(null);
  needToUpdateCalc = false;

  constructor() {
    this.environment = this.envSvc.get();
    this.org = this.environment.org;
  }

  private environment;

  private hashFinished = new Subject();
  private org: string;

  hashingDone$ = this.hashFinished.asObservable();

  private clearIwFunds = new Subject();
  clearIWFunds$ = this.hashFinished.asObservable();

  private clear529PickerData = new Subject();
  clear529PickerData$ = this.clear529PickerData.asObservable();

  formValid = signal(true);
  initialErrorsList = [];

  formModified = signal({ modified: false, count: 0, values: {}, app: null });

  private showEvalErrors = false;

  private model = {};
  modelChanged = signal(false);

  private triggerIncomeGroupChange = new Subject();
  triggerIncomeGroupChange$ = this.triggerIncomeGroupChange.asObservable();

  setModel(value): void {
    this.model = value;
    this.modelChanged.set(true);
  }

  setKeyInModel(keys: any[], value: any): any {
    if (!Array.isArray(keys) || keys.length === 0) {
      throw new Error('Keys must be a non-empty array');
    }

    let current = this.model;

    // Traverse the object using all keys except the last one
    for (let i = 0; i < keys.length - 1; i++) {
      const key = this.parseKey(keys[i]);
      if (current[key] === undefined) {
        // Create an empty object or array based on the next key
        const nextKey = this.parseKey(keys[i + 1]);
        current[key] = typeof nextKey === 'number' ? [] : {};
      }
      current = current[key];
    }

    const finalKey = this.parseKey(keys[keys.length - 1]);

    if (value === undefined) {
      delete current[finalKey]; // Delete the final key if value is undefined
    } else {
      current[finalKey] = value; // Set the value at the final key
    }

    this.modelChanged.set(true);
    return this.model;
  }

  // Helper function to parse keys
  private parseKey(key: any): string | number {
    return !isNaN(key) && typeof key !== 'object' ? Number(key) : key;
  }

  getModel(): any {
    if (this.modelChanged()) {
      this.modelChanged.set(false);
    }
    return this.model;
  }

  hashInput(data, hashtype?): Observable<ResultString | StringResult> {
    if (hashtype && hashtype.toLowerCase() === 'md5') {
      this.hashComplete(true);
      return this.rbs.postRequest<ResultString | StringResult>(
        `${this.rbs.baseUrl}/Util/MD5Hash`,
        {
          value: data,
        }
      );
    } else if (hashtype && hashtype.toLowerCase() === 'rsa') {
      this.hashComplete(true);
      return this.rbs.postRequest<ResultString | StringResult>(
        `${this.rbs.baseUrl}/PostTrade/Hash`,
        {
          targetString: data,
          useStaticSalt: true,
        }
      );
    } else {
      this.hashComplete(true);
      return this.rbs.postRequest<ResultString | StringResult>(
        `${this.rbs.baseUrl}/Util/Hash`,
        {
          value: data,
        }
      );
    }
  }

  getLinkedCases(profile): Observable<ApiResponse<Profile[]>> {
    return this.rbs.getRequest<ApiResponse<Profile[]>>(
      `${this.rbs.baseUrl}/Profile/Linked/${profile}`
    );
  }

  swapInsured(
    profile,
    description = ''
  ): Observable<ApiResponse<StringResult>> {
    return this.rbs.getRequest<ApiResponse<StringResult>>(
      `${this.rbs.baseUrl}/Profile/Swap/${profile}?insuredSwap=true&profileDescription=${description}`
    );
  }

  hashComplete(cmplt): void {
    this.hashFinished.next(cmplt);
  }

  clearIwFundData(): void {
    this.clearIwFunds.next('clear');
  }

  clear529Picker(): void {
    const random = Math.floor(Math.random() * 10);
    this.clear529PickerData.next(random);
  }

  setTriggerIncomeGroupChange(employmentStatus: {
    maritalStatus: string;
    spouseEmployment: string;
    includeSpouse: string;
  }): void {
    this.triggerIncomeGroupChange.next(employmentStatus);
  }

  saveUnitProfile(unit, data, app, note?): Observable<void> {
    const sessId = this.ss.get('session');
    const reqHeaders = new HttpHeaders()
      .set('X-RB-S', sessId)
      .set('X-RB-I', app)
      .set('X-RB-O', this.org);
    let url = `${this.rbs.baseUrl}/UnitProfile/UnitProfile?unitId=${unit}`;

    if (note) {
      url += `&note=${note}`;
    }

    return this.rbs.patchRequest<void>(url, data, reqHeaders);
  }

  updateFormValidity(ev) {
    this.initialErrorsList = [];
    if (ev.form.errors && ev.form.errors.length > 0) {
      this.initialErrorsList = [...this.initialErrorsList, ...ev.form.errors];
    }

    Object.entries(ev.form.controls).map(field => {
      if (
        field[0] === 'PurposeRankOrder_OrderedSelection' &&
        field[1]['errors'] &&
        field[1]['touched']
      ) {
        this.initialErrorsList.push(field[1]['errors']);
      } else if (field[1] instanceof FormControl) {
        if (field[1]['errors'] && field[1]['touched']) {
          this.initialErrorsList.push(field[1]['errors']);
        }
      } else if (field[1] instanceof FormArray) {
        field[1]['controls'].forEach(fld => {
          this.processErrors(fld);
        });
      } else if (field[1] instanceof FormGroup) {
        if (field[1]['errors'] && field[1]['touched']) {
          this.initialErrorsList.push(field[1]['errors']);
        }
        Object.entries(field[1]['controls']).forEach(fld => {
          if (fld[1]['errors'] && fld[1]['touched']) {
            this.initialErrorsList.push(fld[1]['errors']);
          }
        });
      }
    });
    const errorsList = this.initialErrorsList.filter(x => {
      return x && x !== undefined;
    });

    const optOut = ev.model.regSkipped;
    const useOptOut =
      (typeof ev.model.UnitRegulation_UseOptOut === 'string' &&
        ev.model.UnitRegulation_UseOptOut === 'Y') ||
      (typeof ev.model.UnitRegulation_UseOptOut === 'boolean' &&
        ev.model.UnitRegulation_UseOptOut);
    // let regState = ev.model.UnitRegulationState_States ? ev.model.UnitRegulationState_States.includes(ev.model.LifeWizard_StateOfIssue) || ev.model.UnitRegulationState_States.includes(ev.model.AnnuityWizard_StateOfIssue) : false;
    // { regRequired: true }

    const regRequiredError = errorsList.some(x => x.regRequired);
    const requiredError = errorsList.some(x => x.required);

    if (optOut && useOptOut && regRequiredError) {
      this.formValid.set(!requiredError);
    } else if (optOut && useOptOut && !regRequiredError && !requiredError) {
      this.formValid.set(true);
    } else {
      this.formValid.set(errorsList.length < 1);
    }
  }

  processErrors(field): void {
    if (Object.getPrototypeOf(field).constructor.name === 'FormGroup') {
      Object.entries(field.controls).forEach(fld => {
        if (fld[1] instanceof FormArray) {
          const fldList = fld[1] as FormArray;
          fldList.controls.forEach(subFld => {
            this.processErrors(subFld);
          });
        } else if (
          (fld[1]['errors'] || fld[1]['status'] === 'INVALID') &&
          fld[1]['touched']
        ) {
          this.initialErrorsList.push(fld[1]['errors']);
        }
      });
    } else if (Object.getPrototypeOf(field).constructor.name === 'FormArray') {
      field.controls.forEach(fld => {
        this.processErrors(fld);
      });
    }
  }

  getFilteredProgramOptions(program, profile): Observable<GroupProfile> {
    const data = { InvestmentValidation: { Program: program } };
    return this.rbs.patchGroupProfile(
      data,
      'FilterAlternativeInvestments',
      profile,
      'iw',
      false
    );
  }

  setUpdateExternalCalcValue(value): void {
    this.updateExternalCalcValue.set(value);
  }

  getNeedToUpdateCalc(): boolean {
    return this.needToUpdateCalc;
  }

  validateFields(fg, group?): void {
    if (
      typeof fg === 'object' &&
      !(fg instanceof FormGroup) &&
      !(fg instanceof FormArray)
    ) {
      Object.keys(fg).forEach(key => {
        const control = fg[key].formControl ? fg[key].formControl : fg[key];
        if (control instanceof FormGroup || control instanceof FormArray) {
          control.markAsTouched();
          control.updateValueAndValidity({ emitEvent: false });
          this.validateFields(control, true);
        } else if (
          !(control instanceof FormControl) &&
          !(control instanceof FormGroup) &&
          !(control instanceof FormArray) &&
          control.type === 'custom-rankorder'
        ) {
          control.formControl.markAsTouched();
        } else {
          if (control instanceof FormControl) {
            control.markAsTouched();
            control.updateValueAndValidity({ emitEvent: false });
          } else if (control.formControl) {
            control.formControl.markAsTouched();
            control.formControl.updateValueAndValidity({ emitEvent: false });
            this.validateFields(control, true);
          }
        }
      });
    } else if (Array.isArray(fg) && !group) {
      fg.forEach(field => {
        const control = field;
        if (control instanceof FormArray) {
          control.markAsTouched();
          control.updateValueAndValidity({ emitEvent: false });
        } else if (
          control instanceof FormGroup ||
          control instanceof FormArray
        ) {
          control.markAsTouched();
          control.updateValueAndValidity({ emitEvent: false });
          this.validateFields(control, true);
        } else if (
          !(control instanceof FormControl) &&
          !(control instanceof FormGroup) &&
          !(control instanceof FormArray) &&
          control.type === 'custom-rankorder'
        ) {
          control.formControl.markAsTouched();
        } else {
          if (control instanceof FormControl) {
            control.markAsTouched();
            control.updateValueAndValidity({ emitEvent: false });
            if (control.invalid) {
              field.setErrors({ required: true });
            }
          } else if (control.formControl) {
            control.formControl.markAsTouched();
            control.formControl.updateValueAndValidity({ emitEvent: false });
            this.validateFields(control, true);
          }
        }
      });
    } else if (group && !(fg instanceof FormControl)) {
      Object.keys(fg.controls).forEach(field => {
        const control = fg.get(field);
        if (control instanceof FormControl) {
          control.markAsTouched();
          control.updateValueAndValidity({ emitEvent: false });
          fg.markAsTouched();
          fg.updateValueAndValidity({ emitEvent: false });
        } else if (
          control instanceof FormGroup ||
          control instanceof FormArray
        ) {
          control.markAsTouched();
          control.updateValueAndValidity({ emitEvent: false });
          this.validateFields(control, true);
        }
      });
    } else if (fg instanceof FormGroup || fg instanceof FormArray) {
      fg.markAsTouched();
      fg.updateValueAndValidity({ emitEvent: false });
      this.validateFields(fg, true);
    } else {
      fg.markAsTouched();
      fg.updateValueAndValidity({ emitEvent: false });
    }
    // this.getErrors(this.field.fieldGroup);
  }
}
